import type {ControllerSpec} from '@forgesworn/joystick-protocol'
import type {ArcadePhoneController} from '../shared/ui/phone-controller'
import {menuPhoneSpec} from '../shared/phone-specs'
import '../shared/ui/phone-controller.css'
import type {PhoneSnapshot} from '../shared/phone-input'
import './style.css'
import { activateDialog } from './dialog-lifecycle'
import { createGamestrCatalogue } from '../renderer/src/leaderboard/gamestr'
import { lookupScore } from './score-lookup'
import { boardFor, parseAnyScoreEvent, parse5555Event, scoreGameId } from '../renderer/src/leaderboard/gamestr-reduce'
import { avatarCss, hexToNpub, lightningDestination, playerIdentity, resolveProfiles, type Profile } from '../renderer/src/leaderboard/profiles'
import type { LeaderboardEntry } from '../shared/types'
import { decodeInvitation, encodeInvitation, invitationTemplate, parseInvitation } from './game-invitation'
import { challengeTemplate, decodeChallenge, encodeChallenge, parseChallenge } from './game-challenge'
import { readSocialState, toggleSocialItem, writeSocialState } from './social-state'
import { requestLightningInvoice, type WebLNProvider } from './lightning-reward'
import { decode as decodeNip19 } from 'nostr-tools/nip19'
import { WEB_EDITION } from './web-edition'
import { connectNostrAccess, currentNostrAccess, currentNostrGameSession, currentNostrSigner, disconnectNostrAccess, NOSTR_SESSION_EVENT, restoreNostrAccess, type ArcadeNostrSigner, type NostrAccess } from './nostr-access'
import { loadWellKnownMembers } from './well-known-members'
import { accountIdentity } from './account-identity'
import { createNostrGameLaunch, installNostrGameBridge, openNostrGame } from './game-auth-handoff'

interface WebGame {
  slug: string; gameId: string; name: string; tagline: string; description?: string; developer?: string
  genres: string[]; url: string; accent: string; hero?: string; logo?: string; artOverlay?: boolean
  featured: boolean; trending: boolean; newRelease: boolean; walletPay: boolean
  phoneController?: 'gamestr-controller-v1'
  controllerProfile?: ControllerSpec
  nostrHandoff?: { protocol: 'gamestr-auth-v1'; editions: string[]; signKinds: number[] }
  players?: { min: number; max: number }; scoreAuthorities?: string[]; scoreKind?: number; scoreField?: string; scoreDir?: 'asc' | 'desc'
}

interface NostrWindow extends Window {
  webln?: WebLNProvider
}

const RELAYS = ['wss://main.relay.gamestr.io', 'wss://relay.trotters.cc', 'wss://nos.lol', 'wss://relay.damus.io']
const GAMESTR_ORIGIN = 'https://gamestr.io'
const app = document.querySelector<HTMLDivElement>('#app')!
const state = {
  scoreLookups: new Map<string, { status: 'syncing' | 'found' | 'invalid' | 'unavailable'; match?: { game: WebGame; entry: LeaderboardEntry } }>(),
  games: [] as WebGame[], query: '', filter: 'all', genre: 'all', selected: null as WebGame | null,
  scores: new Map<string, LeaderboardEntry[]>(), cachedScores: false, relay: 'connecting' as 'connecting' | 'up' | 'down',
  pubkey: '', identityConnecting: false, identityRestoring: true, identityDisconnecting: false, accountMenuOpen: false,
  profiles: new Map<string, Profile>(), profileRequests: new Set<string>(),
  members: new Map<string, string>(),
  social: readSocialState(), activityMode: 'all' as 'all' | 'following',
}

let phone:ArcadePhoneController|null=null
let applyPhoneGamepad: (snapshot:PhoneSnapshot)=>void = ()=> {}
let activePhoneGame: (()=>void)|null = null
const pairPhone=document.createElement('button');pairPhone.type='button';pairPhone.className='arcade-phone-open';pairPhone.textContent='Pair phone';document.body.append(pairPhone)
pairPhone.onclick=async()=> {
  pairPhone.disabled=true
  try {
    const [{mountPhoneController},{startCataloguePhoneInput},browser]=await Promise.all([import('../shared/ui/phone-controller'),import('./phone-catalogue-input'),import('@forgesworn/joystick-protocol/browser')])
    applyPhoneGamepad=browser.applyPhoneGamepad
    phone=mountPhoneController(snapshot=> {if(!activePhoneGame)applyPhoneGamepad(snapshot)})
    const stopInput=startCataloguePhoneInput(()=>!!activePhoneGame)
    window.addEventListener('pagehide',stopInput,{once:true})
    pairPhone.remove();document.querySelector<HTMLButtonElement>('.arcade-phone-open')?.click()
  } catch {pairPhone.disabled=false;pairPhone.textContent='Retry phone pairing'}
}
let phoneGameGeneration=0
const connectPhoneGame=async(child:Window,url:string,profile?:ControllerSpec)=> {
  const generation=++phoneGameGeneration
  activePhoneGame?.();activePhoneGame=null
  if(profile)phone?.setSpec(profile)
  applyPhoneGamepad({connected:false,axes:[0,0,0,0],buttons:Array(17).fill(false)})
  activePhoneGame=()=> {}
  const {bindPhoneGame}=await import('./phone-game-handoff')
  if(generation!==phoneGameGeneration || child.closed)return
  activePhoneGame=bindPhoneGame(child,new URL(url).origin,()=>phone!.snapshot,profile=>phone!.setSpec(profile),()=> {activePhoneGame=null;phone?.setSpec(menuPhoneSpec);if(phone)applyPhoneGamepad(phone.snapshot)})
}
installNostrGameBridge(() => currentNostrSigner())

window.addEventListener(NOSTR_SESSION_EVENT, event => {
  const access = (event as CustomEvent<NostrAccess>).detail
  if (!access) {
    state.pubkey = ''
    state.social = readSocialState()
    state.accountMenuOpen = false
    if (app.childElementCount) render()
    return
  }
  if (!/^[0-9a-f]{64}$/.test(access.pubkey)) return
  state.pubkey = access.pubkey
  state.social = readSocialState(undefined, access.pubkey)
  state.accountMenuOpen = false
  ensureProfile(access.pubkey)
  if (app.childElementCount) render()
})

window.addEventListener('gamestr:nostr-error', event => toast((event as CustomEvent<string>).detail, 'warn'))

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

document.addEventListener('click', event => {
  if (!state.accountMenuOpen) return
  if (event.target instanceof Element && event.target.closest('.identity-menu')) return
  state.accountMenuOpen = false
  render()
})

document.addEventListener('keydown', event => {
  if (event.key !== 'Escape' || !state.accountMenuOpen) return
  state.accountMenuOpen = false
  render()
})

function route(): { name: 'home' | 'scores' | 'developers' | 'game' | 'player' | 'score' | 'invite' | 'challenge'; slug?: string; id?: string } {
  const path = location.pathname.replace(/\/+$/, '') || '/'
  if (path === '/scores') return { name: 'scores' }
  if (path === '/developers') return { name: 'developers' }
  const game = /^\/game\/([a-zA-Z0-9_-]+)$/.exec(path)
  if (game) return { name: 'game', slug: game[1] }
  const player = /^\/player\/([0-9a-f]{64})$/.exec(path)
  if (player) return { name: 'player', id: player[1] }
  const legacyScore = /^\/score\/[0-9a-f]{64}\/[a-zA-Z0-9_-]+\/([0-9a-f]{64})$/.exec(path)
  if (legacyScore) return { name: 'score', id: legacyScore[1] }
  const score = /^\/score\/([0-9a-f]{64})$/.exec(path)
  if (score) return { name: 'score', id: score[1] }
  const invite = /^\/invite\/([A-Za-z0-9_-]{1,4096})$/.exec(path)
  if (invite) return { name: 'invite', id: invite[1] }
  const challenge = /^\/challenge\/([A-Za-z0-9_-]{1,4096})$/.exec(path)
  return challenge ? { name: 'challenge', id: challenge[1] } : { name: 'home' }
}

function toggleFavourite(game: WebGame): void {
  state.social = toggleSocialItem(state.social, 'favourites', game.slug); const saved = writeSocialState(state.social, undefined, state.pubkey); render()
  if (!saved) { toast('Preferences are available for this visit; browser storage is unavailable.', 'warn'); return }
  toast(state.social.favourites.includes(game.slug) ? `${game.name} added to your arcade.` : `${game.name} removed from your arcade.`, 'good')
}

function toggleFollow(pubkey: string): void {
  state.social = toggleSocialItem(state.social, 'follows', pubkey); const saved = writeSocialState(state.social, undefined, state.pubkey); render()
  if (!saved) { toast('Preferences are available for this visit; browser storage is unavailable.', 'warn'); return }
  toast(state.social.follows.includes(pubkey) ? 'Player followed on this device.' : 'Player unfollowed.', 'good')
}

function navigate(path: string): void {
  history.pushState({}, '', path)
  render()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function button(label: string, className: string, action: () => void): HTMLButtonElement {
  const node = el('button', className, label)
  node.type = 'button'
  node.addEventListener('click', action)
  return node
}

function playLink(game: WebGame, label = 'PLAY', className = 'play-small'): HTMLAnchorElement {
  const link = el('a', `${className} button-link play-link`, label)
  link.href = game.url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  const handoff = game.nostrHandoff?.protocol === 'gamestr-auth-v1' && game.nostrHandoff.editions.includes(WEB_EDITION.key) ? game.nostrHandoff : undefined
  if (handoff && state.identityRestoring) {
    link.setAttribute('aria-disabled', 'true')
    link.title = 'Restoring your arcade account before launching with your identity.'
    link.addEventListener('click', event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      event.preventDefault()
      toast('Your arcade account is still restoring. Try Play again when it is ready.', 'warn')
    })
    return link
  }
  const session = handoff ? currentNostrGameSession() : undefined
  link.title = session ? 'Normal click carries your arcade identity; modifier clicks use the game’s own login.' : 'Opens the game with its own login.'
  if (session) {
    const launch = createNostrGameLaunch({
      gameId: game.gameId,
      signKinds: handoff!.signKinds,
      targetUrl: game.url,
      sourceOrigin: location.origin,
      sourceApp: WEB_EDITION.pwaName,
      session,
      profile: state.profiles.get(session.pubkey),
    })
    if (launch) {
      link.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        event.preventDefault()
        if (!openNostrGame(launch,phone?.paired && game.phoneController ? child=>connectPhoneGame(child,game.url,game.controllerProfile) : undefined)) toast('The game popup was blocked. Allow popups to carry your identity, or use a modifier click and sign in inside the game.', 'warn')
      })
    }
  }
  if(game.phoneController && !session) link.addEventListener('click',event=> {
    if(!phone?.paired || event.button!==0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)return
    event.preventDefault()
    const child=window.open('','_blank');if(!child){toast('Allow popups to carry your paired phone into this game.','warn');return}
    connectPhoneGame(child,game.url,game.controllerProfile);child.location.replace(game.url)
  })
  if(phone?.paired && !game.phoneController) link.title += ' Phone pairing across publisher sites is not supported by this game.'
  return link
}

function header(): HTMLElement {
  const head = el('header', 'site-header')
  if (state.pubkey) head.classList.add('has-identity')
  const brand = el('a', 'brand')
  brand.href = '/'
  brand.setAttribute('aria-label', WEB_EDITION.brandAriaLabel)
  brand.addEventListener('click', event => { event.preventDefault(); navigate('/') })
  if (WEB_EDITION.brandGraphic) {
    const graphic = el('img', 'brand-graphic')
    graphic.src = WEB_EDITION.brandGraphic
    graphic.alt = ''
    brand.append(graphic)
  } else brand.append(el('span', 'brand-mark', WEB_EDITION.brandMark))
  brand.append(el('span', 'brand-name', WEB_EDITION.brandName), el('span', 'brand-beta', WEB_EDITION.brandBadge))
  const nav = el('nav', 'site-nav')
  nav.setAttribute('aria-label', 'Primary')
  for (const [label, path] of [['Arcade', '/'], ['Scores', '/scores'], ['Build', '/developers']]) {
    const current = route().name
    const active = (path === '/' && current === 'home') || (path === '/scores' && current === 'scores') || (path === '/developers' && current === 'developers')
    const link = el('a', active ? 'active' : '', label)
    link.href = path
    link.addEventListener('click', event => { event.preventDefault(); navigate(path) })
    nav.append(link)
  }
  const relay = el('span', `relay-pill ${state.relay}`, state.relay === 'up' ? 'NOSTR LIVE' : state.relay === 'down' ? (state.cachedScores ? 'CACHED · RELAYS OFFLINE' : 'RELAYS OFFLINE') : (state.cachedScores ? 'CACHED · CONNECTING' : 'CONNECTING'))
  relay.title = 'Score events are verified locally before display'
  let identity: HTMLElement
  if (state.pubkey) {
    ensureProfile(state.pubkey)
    identity = accountIdentity({
      pubkey: state.pubkey,
      profile: state.profiles.get(state.pubkey),
      open: state.accountMenuOpen,
      busy: state.identityDisconnecting || state.identityConnecting,
      signerStatus: state.identityConnecting ? 'RECONNECTING…' : currentNostrAccess()?.signerStatus === 'unavailable' ? 'SIGNER UNAVAILABLE' : currentNostrAccess()?.canSignEvents ? 'SIGNER CONNECTED' : 'IDENTITY CONNECTED · SIGNER NEEDED',
      onReconnect: () => void connectIdentity(true),
      onSyncPreferences: () => preferenceDialog(),
      onImportDevicePreferences: () => importDevicePreferencesDialog(),
      onToggle: () => { state.accountMenuOpen = !state.accountMenuOpen; render() },
      onProfile: () => { state.accountMenuOpen = false; navigate(`/player/${state.pubkey}`) },
      onLogout: () => void logoutIdentity(),
    })
  } else {
    const connect = button(state.identityRestoring ? 'RESTORING ACCOUNT…' : state.identityConnecting ? 'CONNECTING NOSTR…' : 'CONNECT NOSTR', 'identity-button', () => void connectIdentity())
    connect.disabled = state.identityRestoring || state.identityConnecting || state.identityDisconnecting
    identity = connect
  }
  head.append(brand, nav, relay, identity)
  return head
}

async function connectIdentity(signingRequired = false): Promise<ArcadeNostrSigner | undefined> {
  if (state.identityConnecting) return currentNostrSigner()
  state.identityConnecting = true
  render()
  try {
    const access = await connectNostrAccess({ appName: WEB_EDITION.pwaName, relays: RELAYS, signingRequired })
    if (!access) return undefined
    toast(access.canSignEvents ? 'Nostr signer connected.' : 'Nostr identity connected. Signing can be connected when needed.', 'good')
    return currentNostrSigner()
  } catch (error) {
    toast(error instanceof Error ? `Nostr login failed: ${error.message}` : 'Nostr login was cancelled or refused.', 'warn')
    return undefined
  } finally {
    state.identityConnecting = false
    render()
  }
}

async function logoutIdentity(): Promise<void> {
  if (state.identityDisconnecting) return
  state.identityDisconnecting = true
  render()
  try {
    await disconnectNostrAccess()
    state.accountMenuOpen = false
    toast('Logged out of Nostr.', 'good')
  } catch {
    toast('Nostr logout failed. Please retry.', 'warn')
  } finally {
    state.identityDisconnecting = false
    render()
  }
}

function hero(games: WebGame[]): HTMLElement {
  const featured = games.find(game => game.featured && game.hero) ?? games.find(game => game.hero) ?? games[0]
  const heroArtwork = games.find(game => game.slug === WEB_EDITION.heroGameSlug && game.hero) ?? featured
  const section = el('section', 'hero')
  if (heroArtwork?.hero) section.style.setProperty('--hero-image', `url("${heroArtwork.hero}")`)
  const copy = el('div', 'hero-copy')
  copy.append(el('p', 'kicker', WEB_EDITION.heroKicker), el('h1', '', WEB_EDITION.heroTitle))
  copy.append(el('p', 'hero-lede', WEB_EDITION.heroDescription))
  const actions = el('div', 'hero-actions')
  actions.append(button('EXPLORE GAMES', 'primary', () => document.querySelector('#games')?.scrollIntoView({ behavior: 'smooth' })))
  if (featured) actions.append(playLink(featured, `PLAY ${featured.name.toUpperCase()} ↗`, 'secondary'))
  copy.append(actions)
  if (WEB_EDITION.key === 'gamestr') copy.append(el('p', 'launch-login-note', 'Games open on their publisher’s site and may ask you to sign in there.'))
  const proof = el('dl', 'proof-strip')
  for (const [value, label] of [[String(games.length), 'PLAYABLE GAMES'], ['2', 'SCORE KINDS'], ['0', 'CUSTODIAL ACCOUNTS']]) {
    const item = el('div'); item.append(el('dt', '', value), el('dd', '', label)); proof.append(item)
  }
  copy.append(proof)
  section.append(copy)
  if (WEB_EDITION.heroGraphic) {
    const graphic = el('img', 'edition-hero-graphic')
    graphic.src = WEB_EDITION.heroGraphic
    graphic.alt = WEB_EDITION.heroGraphicAlt
    section.append(graphic)
  }
  return section
}

function filters(): HTMLElement {
  const wrap = el('section', 'discovery-controls')
  const search = el('label', 'search-box')
  search.append(el('span', 'sr-only', 'Search games'))
  const input = el('input')
  input.id = 'arcade-search'; input.type = 'search'; input.placeholder = 'Search games, genres, developers…'; input.value = state.query
  input.addEventListener('input', () => { state.query = input.value; renderGameGrid() })
  search.append(input)
  const tabs = el('div', 'filter-tabs'); tabs.setAttribute('role', 'group'); tabs.setAttribute('aria-label', 'Game filters')
  for (const [key, label] of [['all', 'ALL'], ['favourites', `MY ARCADE${state.social.favourites.length ? ` (${state.social.favourites.length})` : ''}`], ['featured', 'FEATURED'], ['trending', 'TRENDING'], ['new', 'NEW']]) {
    const tab = button(label, state.filter === key ? 'selected' : '', () => { state.filter = key; renderGameGrid() })
    tab.id = `game-filter-${key}`; tab.dataset.filter = key; tab.setAttribute('aria-pressed', String(state.filter === key)); tabs.append(tab)
  }
  const genres = Array.from(new Set(state.games.flatMap(game => game.genres.map(g => g.toLowerCase())))).sort()
  const select = el('select'); select.id = 'game-genre'; select.setAttribute('aria-label', 'Filter by genre')
  for (const genre of ['all', ...genres]) { const option = el('option', '', genre === 'all' ? 'ALL GENRES' : genre.toUpperCase()); option.value = genre; option.selected = genre === state.genre; select.append(option) }
  select.addEventListener('change', () => { state.genre = select.value; renderGameGrid() })
  wrap.append(search, tabs, select)
  return wrap
}

function filteredGames(): WebGame[] {
  const q = state.query.trim().toLowerCase()
  return state.games.filter(game => {
    if (state.filter === 'featured' && !game.featured) return false
    if (state.filter === 'favourites' && !state.social.favourites.includes(game.slug)) return false
    if (state.filter === 'trending' && !game.trending) return false
    if (state.filter === 'new' && !game.newRelease) return false
    if (state.genre !== 'all' && !game.genres.some(g => g.toLowerCase() === state.genre)) return false
    return !q || [game.name, game.tagline, game.developer, ...game.genres].filter(Boolean).some(value => String(value).toLowerCase().includes(q))
  })
}

function gameCard(game: WebGame): HTMLElement {
  const card = el('article', 'game-card')
  card.style.setProperty('--accent', game.accent)
  const art = el('button', 'game-art'); art.id = `view-game-${game.slug}`; art.type = 'button'; art.setAttribute('aria-label', `View ${game.name}`)
  if (game.hero) art.style.backgroundImage = `linear-gradient(180deg, transparent 20%, rgba(8,9,13,.92)), url("${game.hero}")`
  if (game.logo) { const img = el('img'); img.src = game.logo; img.alt = ''; img.loading = 'lazy'; art.append(img) }
  else if (game.artOverlay !== false) art.append(el('span', 'wordmark', game.name))
  art.addEventListener('click', () => navigate(`/game/${game.slug}`))
  const flags = el('div', 'card-flags')
  if (game.featured) flags.append(el('span', '', 'FEATURED'))
  if (game.trending) flags.append(el('span', '', 'TRENDING'))
  if (game.newRelease) flags.append(el('span', '', 'NEW'))
  art.append(flags)
  const body = el('div', 'game-body')
  const titleRow = el('div', 'title-row'); titleRow.append(el('h3', '', game.name))
  const cardActions = el('div', 'card-actions')
  const favourite = button(state.social.favourites.includes(game.slug) ? '★' : '☆', 'favourite-button', () => toggleFavourite(game)); favourite.id = `favourite-card-${game.slug}`; favourite.setAttribute('aria-label', `${state.social.favourites.includes(game.slug) ? 'Remove' : 'Add'} ${game.name} ${state.social.favourites.includes(game.slug) ? 'from' : 'to'} favourites`)
  const play = playLink(game); play.id = `play-card-${game.slug}`
  cardActions.append(favourite, play); titleRow.append(cardActions)
  body.append(titleRow, el('p', '', game.tagline))
  const tags = el('div', 'tags'); game.genres.slice(0, 3).forEach(genre => tags.append(el('span', '', genre.toUpperCase())))
  const scores = state.scores.get(game.gameId) ?? []
  const latest = [...scores].sort((a, b) => b.at - a.at)[0]
  if (latest) tags.append(el('span', 'score-chip', `LIVE · ${latest.score.toLocaleString()}`))
  body.append(tags); card.append(art, body)
  return card
}

function renderGameGrid(): void {
  const focused = document.activeElement instanceof HTMLElement ? document.activeElement : undefined
  const grid = document.querySelector<HTMLElement>('#game-grid')
  const count = document.querySelector<HTMLElement>('#game-count')
  if (!grid || !count) return
  for (const tab of document.querySelectorAll<HTMLButtonElement>('.filter-tabs button')) { const selected = tab.dataset.filter === state.filter; tab.classList.toggle('selected', selected); tab.setAttribute('aria-pressed', String(selected)) }
  const games = filteredGames(); count.textContent = `${games.length} GAME${games.length === 1 ? '' : 'S'}`
  grid.replaceChildren(...games.map(gameCard))
  if (!games.length) grid.append(el('p', 'empty-state', 'No games match that filter. Try clearing the search.'))
  if (focused?.id && !focused.isConnected) document.getElementById(focused.id)?.focus({ preventScroll: true })
}

function refreshActivityPanel(): void {
  const focused = document.activeElement instanceof HTMLElement ? document.activeElement : undefined
  document.querySelector('.activity-panel')?.replaceWith(activityPanel())
  if (focused?.id && !focused.isConnected) document.getElementById(focused.id)?.focus({ preventScroll: true })
}

function activityPanel(): HTMLElement {
  const aside = el('aside', 'activity-panel')
  const heading = el('div', 'section-heading'); heading.append(el('div', '', state.activityMode === 'following' ? 'FOLLOWING' : 'GAMESTR.IO LIVE SCORES'), externalLink('GAMESTR.IO ↗', `${GAMESTR_ORIGIN}/scores`))
  const modes = el('div', 'activity-modes'); modes.setAttribute('role', 'group'); modes.setAttribute('aria-label', 'Activity filter')
  for (const [mode, label] of [['all', 'ALL'], ['following', `FOLLOWING ${state.social.follows.length ? `(${state.social.follows.length})` : ''}`]] as const) {
    const modeButton = button(label, state.activityMode === mode ? 'selected' : '', () => { state.activityMode = mode; refreshActivityPanel() })
    modeButton.id = `activity-filter-${mode}`
    modeButton.setAttribute('aria-pressed', String(state.activityMode === mode))
    modes.append(modeButton)
  }
  const list = el('ol', 'activity-list')
  const latest = state.games.flatMap(game => (state.scores.get(game.gameId) ?? []).map(score => ({ game, score })))
    .filter(({ score }) => state.activityMode === 'all' || state.social.follows.includes(score.pubkey))
    .sort((a, b) => b.score.at - a.score.at).slice(0, 8)
  if (!latest.length) list.append(el('li', 'activity-empty', state.activityMode === 'following' && !state.social.follows.length ? 'Follow players from their profile to build your activity feed.' : state.relay === 'down' ? 'Relays are unavailable. Retrying…' : 'Listening for verified score events…'))
  latest.forEach(({ game, score }) => {
    ensureProfile(score.pubkey)
    const profile = state.profiles.get(score.pubkey)
    const identity = playerIdentity(score.pubkey, score, profile)
    const item = el('li'); const avatar = el('span', 'mini-avatar', identity.label.slice(0, 2).toUpperCase())
    avatar.style.background = avatarCss(score.pubkey)
    if (profile?.picture) { const image = el('img'); image.src = profile.picture; image.alt = ''; avatar.replaceChildren(image) }
    const text = el('span')
    const nameRow = el('span', 'player-primary-row'); nameRow.append(el('strong', '', identity.label))
    const badge = memberBadge(score.pubkey); if (badge) nameRow.append(badge)
    text.append(nameRow)
    if (identity.nip05) text.append(el('small', 'player-nip05', identity.nip05))
    text.append(el('small', '', `${game.name} · ${new Date(score.at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`))
    item.append(avatar, text, el('b', '', score.score.toLocaleString())); list.append(item)
  })
  const trust = el('div', 'trust-note'); trust.append(el('strong', '', 'GAMESTR.IO · VERIFIED LOCALLY'), el('p', '', 'These are Gamestr.io score events from Nostr. Every signature is checked in your browser; invalid and explicitly cheated events never reach the board.'))
  aside.append(heading, modes, list, trust); return aside
}

function linkButton(label: string, path: string): HTMLAnchorElement {
  const link = el('a', 'text-link', label); link.href = path; link.addEventListener('click', e => { e.preventDefault(); navigate(path) }); return link
}

function externalLink(label: string, url: string, className = 'text-link'): HTMLAnchorElement {
  const link = el('a', className, label); link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer'; return link
}

function playerLink(player: string | LeaderboardEntry, className = 'player'): HTMLAnchorElement {
  const entry = typeof player === 'string' ? undefined : player
  const pubkey = typeof player === 'string' ? player : player.pubkey
  ensureProfile(pubkey)
  const identity = playerIdentity(pubkey, entry, state.profiles.get(pubkey))
  const link = el('a', className)
  const primary = el('span', 'player-primary-row'); primary.append(el('span', 'player-primary', identity.label))
  const badge = memberBadge(pubkey); if (badge) primary.append(badge)
  link.append(primary)
  if (identity.nip05) link.append(el('small', 'player-nip05', identity.nip05))
  link.href = `/player/${pubkey}`
  link.addEventListener('click', event => { event.preventDefault(); navigate(link.getAttribute('href')!) })
  return link
}

function memberBadge(pubkey: string): HTMLElement | undefined {
  if (WEB_EDITION.key !== '600') return undefined
  const name = state.members.get(pubkey.toLowerCase())
  if (!name) return undefined
  const badge = el('span', 'member-badge', '600B ✓')
  const description = `Verified 600.wtf well-known member: ${name}@600.wtf`
  badge.title = description
  badge.setAttribute('aria-label', description)
  return badge
}

function scoreLink(entry: LeaderboardEntry): HTMLElement {
  if (!entry.eventId) return el('strong', '', entry.score.toLocaleString())
  const link = el('a', 'score-link', entry.score.toLocaleString())
  link.href = `/score/${entry.eventId}`
  link.addEventListener('click', event => { event.preventDefault(); navigate(link.getAttribute('href')!) })
  return link
}

function home(): HTMLElement {
  const main = el('main'); main.id = 'main'; main.append(hero(state.games))
  const layout = el('div', 'content-layout')
  const catalogue = el('div', 'catalogue'); catalogue.id = 'games'; catalogue.append(filters())
  const title = el('div', 'section-heading'); title.append(el('h2', '', 'ARCADE FLOOR'), el('span', '', '')); title.lastElementChild!.id = 'game-count'
  const grid = el('div', 'game-grid'); grid.id = 'game-grid'; catalogue.append(title, grid)
  layout.append(catalogue, activityPanel()); main.append(layout, developerCallout()); setTimeout(renderGameGrid)
  return main
}

function developerCallout(): HTMLElement {
  const section = el('section', 'developer-callout')
  const copy = el('div'); copy.append(el('p', 'kicker', 'SHIP ON AN OPEN ARCADE'), el('h2', '', 'One manifest. Global discovery. No permission slip.'), el('p', '', 'Publish scores to Nostr, keep hosting your own game, and become playable across web and physical cabinets.'))
  copy.append(button('READ THE DEVELOPER GUIDE', 'primary', () => navigate('/developers')))
  const code = el('pre'); code.textContent = '{\n  "manifestVersion": 2,\n  "gameId": "your-game",\n  "url": "https://play.example",\n  "capabilities": { "nostrSign": true }\n}'
  section.append(copy, code); return section
}

function scoreHistoryNote(): HTMLElement {
  return el('p', 'score-history-note', 'Rankings use scores received from the relays, including retained personal bests. Complete history has not been established; earlier or missing scores may change the rankings.')
}

function scoresPage(): HTMLElement {
  const main = el('main', 'page'); main.id = 'main'
  main.append(el('p', 'kicker', 'GAMESTR.IO · CRYPTOGRAPHICALLY VERIFIED'), el('h1', '', 'Live score network'), el('p', 'page-lede', 'Gamestr.io scores, assembled from signed Nostr events rather than a platform database. Profiles come from each player’s signed kind-0 metadata, with the game-signed player name as the fallback.'), externalLink('OPEN THE SCORE NETWORK ON GAMESTR.IO ↗', `${GAMESTR_ORIGIN}/scores`, 'button-link gamestr-source-link'))
  const grid = el('div', 'score-boards')
  for (const game of state.games) {
    const entries = state.scores.get(game.gameId) ?? []
    const board = boardFor(entries, 'all', 5, Math.floor(Date.now() / 1000), game.scoreDir ?? 'desc')
    if (!board.length) continue
    const section = el('section', 'score-board'); const heading = el('div', 'section-heading'); heading.append(el('h2', '', game.name), playLink(game)); section.append(heading)
    const list = el('ol'); board.forEach((entry, index) => { const row = el('li'); row.append(el('span', 'rank', String(index + 1).padStart(2, '0')), playerLink(entry), scoreLink(entry)); list.append(row) }); section.append(list); grid.append(section)
  }
  if (!grid.children.length) grid.append(el('p', 'empty-state', 'Verified boards are syncing from Nostr relays…'))
  main.append(scoreHistoryNote(), grid); return main
}

let developerPageElement: HTMLElement | undefined

function developersPage(): HTMLElement {
  if (developerPageElement) return developerPageElement
  const main = el('main', 'page prose'); main.id = 'main'; developerPageElement = main
  main.append(el('p', 'kicker', 'DEVELOPER GUIDE'), el('h1', '', 'Bring your game. Keep your sovereignty.'), el('p', 'page-lede', 'Gamestr is discovery and interoperability, not a hosting lock-in. Your URL, your code, your revenue, your player relationship.'))
  const steps = [
    ['1', 'Declare the game', 'Add a Manifest v2 file with a stable gameId, HTTPS URL, genres, art, controls, and only the capabilities you actually need.'],
    ['2', 'Publish signed scores', 'Emit kind 30762 with game and score tags, or kind 5555 with a declared score field. Sign as the player or use a clearly documented game authority.'],
    ['3', 'Submit without surrendering hosting', 'Validate below, approve with Signet or your Nostr signer, and publish a portable NIP-89 submission. Curated listing remains an explicit review; your game stays on your origin.'],
  ]
  const list = el('div', 'dev-steps'); steps.forEach(([number, title, body]) => { const item = el('section'); item.append(el('b', '', number), el('h2', '', title), el('p', '', body)); list.append(item) }); main.append(list)
  const event = el('pre'); event.textContent = JSON.stringify({ kind: 30762, tags: [['game', 'your-game'], ['score', '4200'], ['sats', '21']], content: '' }, null, 2)
  const safety = el('section', 'dev-safety'); safety.append(el('h2', '', 'Trust boundary'), el('p', '', 'The web app validates structure and Schnorr signatures, bounds retained events, ignores cheated scores, and never asks for an nsec. Play links open the reviewed publisher origin directly in a separate tab.'))
  const studioHost = el('div'); studioHost.id = 'manifest-studio-host'
  main.append(el('h2', '', 'Canonical score event'), event, safety, studioHost)
  setTimeout(() => {
    const host = document.querySelector<HTMLElement>('#manifest-studio-host')
    if (host) void import('./developer-studio').then(({ mountDeveloperStudio }) => mountDeveloperStudio(host, RELAYS))
  })
  return main
}

function gamePage(slug: string): HTMLElement {
  let identifier = slug
  if (slug.startsWith('naddr1')) {
    try { const decoded = decodeNip19(slug); if (decoded.type === 'naddr') identifier = decoded.data.identifier } catch { /* invalid legacy link */ }
  }
  const aliases: Record<string, string> = { 'miner-v1': 'nogames-miner-v1', 'snake-v1': 'nogames-snake-v1' }
  identifier = aliases[identifier] ?? identifier
  const game = state.games.find(item => item.slug === identifier || item.gameId === identifier)
  if (!game) { const main = el('main', 'page retired-game'); main.id = 'main'; main.append(el('p', 'kicker', 'LEGACY GAME LINK'), el('h1', '', 'This cabinet moved on'), el('p', 'page-lede', 'The signed legacy link is recognised, but that game is not in the reviewed web catalogue. Nothing was silently substituted.'), linkButton('BROWSE CURRENT GAMES', '/')); return main }
  const main = el('main', 'game-detail'); main.id = 'main'; main.style.setProperty('--accent', game.accent)
  const art = el('div', 'detail-art')
  if (game.hero) art.style.backgroundImage = `linear-gradient(90deg, rgba(8,9,13,.2), #08090d), url("${game.hero}")`
  if (game.logo) { const image = el('img'); image.src = game.logo; image.alt = `${game.name} logo`; art.append(image) }
  const copy = el('div', 'detail-copy'); copy.append(el('p', 'kicker', game.genres.join(' · ').toUpperCase()), el('h1', '', game.name), el('p', 'page-lede', game.description ?? game.tagline))
  const favourite = button(state.social.favourites.includes(game.slug) ? '★ IN MY ARCADE' : '☆ ADD TO MY ARCADE', 'secondary', () => toggleFavourite(game)); favourite.id = `favourite-detail-${game.slug}`
  const actions = el('div', 'hero-actions'); actions.append(playLink(game, 'PLAY ON PUBLISHER SITE ↗', 'primary'), favourite, button('INVITE TO PLAY', 'secondary', () => void shareInvitation(game)), button('CREATE CHALLENGE', 'secondary', () => createChallengeDialog(game))); copy.append(actions)
  if(game.phoneController) copy.append(el('p','phone-controller-note','Your paired phone stays connected when this game opens.'))
  else copy.append(el('p','phone-controller-note','Phone pairing works in the arcade catalogue. This publisher has not enabled phone control across sites.'))
  if (!game.nostrHandoff?.editions.includes(WEB_EDITION.key)) copy.append(el('p', 'launch-login-note', 'This game opens on its publisher’s site and uses its own login. Your arcade login is not passed to it.'))
  const facts = el('dl', 'game-facts'); for (const [label, value] of [['IDENTITY', 'NOSTR'], ['SCORES', 'VERIFIED'], ['WALLET', game.walletPay ? 'LIGHTNING' : 'OPTIONAL'], ['PLAYERS', game.players ? `${game.players.min}–${game.players.max}` : '1']]) { const item = el('div'); item.append(el('dt', '', label), el('dd', '', value)); facts.append(item) } copy.append(facts)
  main.append(art, copy)
  const board = el('section', 'detail-board')
  const boardHeading = el('div', 'section-heading'); boardHeading.append(el('h2', '', 'GAMESTR.IO LEADERBOARD'), externalLink('OPEN ON GAMESTR.IO ↗', `${GAMESTR_ORIGIN}/${encodeURIComponent(game.gameId)}`)); board.append(boardHeading)
  board.append(scoreHistoryNote())
  const entries = boardFor(state.scores.get(game.gameId) ?? [], 'all', 10, Math.floor(Date.now() / 1000), game.scoreDir ?? 'desc')
  const list = el('ol'); entries.forEach((entry, index) => { const item = el('li'); item.append(el('span', 'rank', String(index + 1).padStart(2, '0')), playerLink(entry), scoreLink(entry)); list.append(item) }); if (!entries.length) list.append(el('li', 'activity-empty', 'Syncing verified Gamestr.io scores…')); board.append(list); main.append(board)
  return main
}

const queuedProfiles = new Set<string>()
let profileBatchTimer: number | undefined
let profileRenderTimer: number | undefined

function scheduleProfileRender(): void {
  if (profileRenderTimer !== undefined) return
  profileRenderTimer = window.setTimeout(() => {
    profileRenderTimer = undefined
    render()
  }, 0)
}

function ensureProfile(pubkey: string): void {
  if (!/^[0-9a-f]{64}$/.test(pubkey) || state.profiles.has(pubkey) || state.profileRequests.has(pubkey)) return
  state.profileRequests.add(pubkey)
  queuedProfiles.add(pubkey)
  if (profileBatchTimer !== undefined) return
  profileBatchTimer = window.setTimeout(() => {
    profileBatchTimer = undefined
    const pubkeys = Array.from(queuedProfiles)
    queuedProfiles.clear()
    const dispose = resolveProfiles(RELAYS, pubkeys, (resolved, profile) => {
      state.profiles.set(resolved, profile)
      scheduleProfileRender()
    })
    setTimeout(dispose, 6_000)
  }, 0)
}

function latestPlayerEntry(pubkey: string): LeaderboardEntry | undefined {
  return state.games.flatMap(game => state.scores.get(game.gameId) ?? [])
    .filter(entry => entry.pubkey === pubkey)
    .sort((a, b) => b.at - a.at)[0]
}

function zapSetupNote(): HTMLElement {
  return el('p', 'zap-setup-note', 'To receive zaps, players need to add a lightning address (lud16) or LNURL (lud06) to their Nostr profile.')
}

function playerPage(pubkey: string): HTMLElement {
  ensureProfile(pubkey)
  const profile = state.profiles.get(pubkey)
  const scoreIdentity = latestPlayerEntry(pubkey)
  const identityLabel = playerIdentity(pubkey, scoreIdentity, profile)
  const main = el('main', 'page player-page'); main.id = 'main'
  const identity = el('section', 'player-identity')
  const avatar = el('div', 'player-avatar')
  if (profile?.picture) { const image = el('img'); image.src = profile.picture; image.alt = ''; avatar.append(image) }
  else avatar.style.background = avatarCss(pubkey)
  const copy = el('div'); copy.append(el('p', 'kicker', 'GAMESTR.IO PLAYER'), el('h1', '', identityLabel.label))
  if (identityLabel.nip05) copy.append(el('p', 'player-profile-nip05', identityLabel.nip05))
  copy.append(el('p', 'player-npub', hexToNpub(pubkey)))
  const external = el('a', 'button-link', 'VIEW ON NOSTR'); external.href = `https://njump.me/${hexToNpub(pubkey)}`; external.target = '_blank'; external.rel = 'noopener noreferrer'; copy.append(external)
  copy.append(externalLink('VIEW ON GAMESTR.IO ↗', `${GAMESTR_ORIGIN}/player/${pubkey}`, 'button-link gamestr-source-link'))
  if (pubkey !== state.pubkey) { const follow = button(state.social.follows.includes(pubkey) ? '✓ FOLLOWING' : '+ FOLLOW PLAYER', 'secondary follow-button', () => toggleFollow(pubkey)); follow.id = `follow-player-${pubkey}`; copy.append(follow) }
  if (lightningDestination(profile) && pubkey !== state.pubkey) copy.append(button('⚡ ZAP PLAYER', 'reward-button', () => rewardDialog(pubkey, profile!)))
  else if (pubkey !== state.pubkey) copy.append(zapSetupNote())
  identity.append(avatar, copy); main.append(identity)

  if (pubkey === state.pubkey) {
    const favouriteGames = state.games.filter(game => state.social.favourites.includes(game.slug))
    const favourites = el('section', 'player-favourites'); favourites.append(el('h2', '', 'MY ARCADE'))
    const grid = el('div', 'game-grid'); grid.append(...favouriteGames.map(gameCard)); if (!favouriteGames.length) grid.append(el('p', 'empty-state', 'Favourite games appear here. Add them from the arcade or a game page.'))
    favourites.append(grid); main.append(favourites)
  }

  const bests = state.games.map(game => {
    const entries = (state.scores.get(game.gameId) ?? []).filter(entry => entry.pubkey === pubkey)
    return { game, entry: boardFor(entries, 'all', 1, Math.floor(Date.now() / 1000), game.scoreDir ?? 'desc')[0] }
  }).filter(item => item.entry)
  const section = el('section', 'player-bests'); section.append(el('h2', '', 'PERSONAL BESTS'))
  section.append(scoreHistoryNote())
  const grid = el('div', 'score-boards')
  bests.forEach(({ game, entry }) => { const card = el('article', 'personal-best'); card.append(el('span', '', game.name), scoreLink(entry), playLink(game)); grid.append(card) })
  if (!bests.length) grid.append(el('p', 'empty-state', 'No verified scores have synced for this player yet.'))
  section.append(grid); main.append(section); return main
}

function scorePage(eventId: string): HTMLElement {
  let match: { game: WebGame; entry: LeaderboardEntry } | undefined
  for (const game of state.games) {
    const entry = (state.scores.get(game.gameId) ?? []).find(candidate => candidate.eventId === eventId)
    if (entry) { match = { game, entry }; break }
  }
  const main = el('main', 'page score-detail'); main.id = 'main'
  let lookup = state.scoreLookups.get(eventId)
  if (!match && !lookup) {
    if (state.scoreLookups.size >= 32) state.scoreLookups.delete(state.scoreLookups.keys().next().value!)
    lookup = { status: 'syncing' }; state.scoreLookups.set(eventId, lookup)
    void lookupScore(eventId, RELAYS).then(result => {
      let loaded: { game: WebGame; entry: LeaderboardEntry } | undefined
      if (result.status === 'found') {
        const game = state.games.find(candidate => candidate.gameId === scoreGameId(result.event) && (candidate.scoreKind ?? 30762) === result.event.kind)
        const entry = game ? result.event.kind === 5555 ? parse5555Event(result.event, game.gameId, game.scoreField ?? 'score', { [game.gameId]: game.scoreAuthorities ?? [] }) : parseAnyScoreEvent(result.event, { [game.gameId]: game.scoreAuthorities ?? [] })?.entry : null
        if (game && entry) loaded = { game, entry }
      }
      state.scoreLookups.set(eventId, loaded ? { status: 'found', match: loaded } : { status: result.status === 'found' ? 'invalid' : result.status })
      if (route().name === 'score' && route().id === eventId) render()
    })
  }
  match ??= lookup?.match
  if (!match) {
    const status = lookup?.status ?? 'unavailable'
    main.append(el('p', 'kicker', 'SCORE LOOKUP'), el('h1', '', status === 'syncing' ? 'Looking up this event…' : status === 'invalid' ? 'Score failed validation' : 'Score unavailable'), el('p', 'page-lede', status === 'syncing' ? 'Requesting this exact event from the configured relays.' : status === 'invalid' ? 'The returned event is invalid, has untrusted player attribution, or belongs outside this catalogue.' : 'The configured relays did not return this event. This does not prove the score never existed.'))
    if (status !== 'syncing') main.append(button('RETRY LOOKUP', 'secondary', () => { state.scoreLookups.delete(eventId); render() }))
    return main
  }
  ensureProfile(match.entry.pubkey)
  const profile = state.profiles.get(match.entry.pubkey)
  const identity = playerIdentity(match.entry.pubkey, match.entry, profile)
  main.append(el('p', 'kicker', 'VERIFIED GAMESTR.IO SCORE'), el('h1', '', match.entry.score.toLocaleString()), el('p', 'page-lede', `${match.game.name} · ${new Date(match.entry.at * 1000).toLocaleString()}`))
  const facts = el('dl', 'score-facts')
  const factValues = [['PLAYER', identity.label], ...(identity.nip05 ? [['NIP-05', identity.nip05]] : []), ['REPORTED SATS', String(match.entry.sats ?? 0)], ['ATTRIBUTION', match.entry.attribution === 'game-authority' ? 'REVIEWED GAME AUTHORITY' : 'PLAYER SIGNED'], ['SIGNATURE', 'VALID'], ['EVENT', eventId]]
  for (const [label, value] of factValues) { const item = el('div'); item.append(el('dt', '', label), el('dd', '', value)); facts.append(item) }
  main.append(el('p', 'page-lede', 'The signature verifies the event author and attribution. It does not prove honest gameplay or Lightning settlement.'));
  main.append(facts, playerLink(match.entry, 'button-link'), playLink(match.game, `PLAY ${match.game.name.toUpperCase()} ↗`, 'primary'), externalLink('VIEW ORIGINAL ON GAMESTR.IO ↗', `${GAMESTR_ORIGIN}/${encodeURIComponent(match.game.gameId)}/score/${eventId}`, 'button-link gamestr-source-link'))
  if (lightningDestination(profile) && match.entry.pubkey !== state.pubkey) main.append(button('⚡ ZAP THIS SCORE', 'reward-button', () => rewardDialog(match!.entry.pubkey, profile!)))
  else if (match.entry.pubkey !== state.pubkey) main.append(zapSetupNote())
  return main
}

function invitationPage(encoded: string): HTMLElement {
  const main = el('main', 'page invitation-page'); main.id = 'main'
  const signed = decodeInvitation(encoded)
  const invitation = signed ? parseInvitation(signed) : undefined
  const game = invitation ? state.games.find(candidate => candidate.gameId === invitation.gameId && candidate.url === invitation.gameUrl) : undefined
  if (!invitation || !game) {
    main.append(el('p', 'kicker', 'SIGNED GAME INVITATION'), el('h1', '', 'Invitation unavailable'), el('p', 'page-lede', 'This link is invalid, expired, or points outside the reviewed Gamestr catalogue.'), linkButton('BROWSE THE ARCADE', '/'))
    return main
  }
  ensureProfile(invitation.event.pubkey)
  const inviter = playerIdentity(invitation.event.pubkey, undefined, state.profiles.get(invitation.event.pubkey)).label
  const card = el('section', 'invitation-card'); card.style.setProperty('--accent', game.accent)
  card.append(el('span', 'invite-proof', '✓ SIGNATURE VERIFIED'), el('p', 'kicker', `${inviter} INVITED YOU`), el('h1', '', game.name), el('p', 'page-lede', game.tagline))
  const facts = el('dl', 'game-facts')
  for (const [label, value] of [['FROM', inviter], ['EXPIRES', new Date(invitation.expiresAt * 1000).toLocaleDateString()], ['GAME', game.gameId], ['ORIGIN', new URL(game.url).host]]) { const item = el('div'); item.append(el('dt', '', label), el('dd', '', value)); facts.append(item) }
  const actions = el('div', 'hero-actions'); actions.append(playLink(game, 'ACCEPT & PLAY ↗', 'primary'), playerLink(invitation.event.pubkey, 'button-link'), linkButton('NOT NOW', '/'))
  card.append(facts, actions); main.append(card); return main
}

function challengePage(encoded: string): HTMLElement {
  const main = el('main', 'page challenge-page'); main.id = 'main'
  const signed = decodeChallenge(encoded)
  const challenge = signed ? parseChallenge(signed) : undefined
  const game = challenge ? state.games.find(candidate => candidate.gameId === challenge.gameId && candidate.url === challenge.gameUrl) : undefined
  if (!challenge || !game) {
    main.append(el('p', 'kicker', 'SIGNED CHALLENGE'), el('h1', '', 'Challenge unavailable'), el('p', 'page-lede', 'This challenge is invalid, too old, or points outside the reviewed Gamestr catalogue.'), linkButton('BROWSE THE ARCADE', '/'))
    return main
  }
  ensureProfile(challenge.event.pubkey)
  const creator = playerIdentity(challenge.event.pubkey, undefined, state.profiles.get(challenge.event.pubkey)).label
  const now = Math.floor(Date.now() / 1000)
  const phase = now < challenge.startsAt ? 'UPCOMING' : now <= challenge.endsAt ? 'LIVE NOW' : 'FINAL'
  const hero = el('section', 'challenge-hero'); hero.style.setProperty('--accent', game.accent)
  hero.append(el('span', 'invite-proof', '✓ SIGNED CHALLENGE'), el('p', 'kicker', `${phase} · ${game.name.toUpperCase()}`), el('h1', '', challenge.name), el('p', 'page-lede', `Created by ${creator}. Every standing comes from a locally verified Nostr score inside the signed time window.`))
  const facts = el('dl', 'game-facts')
  for (const [label, value] of [['STATUS', phase], ['START', new Date(challenge.startsAt * 1000).toLocaleString()], ['END', new Date(challenge.endsAt * 1000).toLocaleString()], ['ORIGIN', new URL(game.url).host]]) { const item = el('div'); item.append(el('dt', '', label), el('dd', '', value)); facts.append(item) }
  const actions = el('div', 'hero-actions'); actions.append(playLink(game, phase === 'FINAL' ? 'PLAY THIS GAME ↗' : 'ENTER & PLAY ↗', 'primary'), playerLink(challenge.event.pubkey, 'button-link')); hero.append(facts, actions); main.append(hero)
  const standings = el('section', 'challenge-standings'); const heading = el('div', 'section-heading'); heading.append(el('h2', '', phase === 'FINAL' ? 'OBSERVED STANDINGS' : 'LIVE STANDINGS'), el('span', '', `${new Date(challenge.startsAt * 1000).toLocaleDateString()} - ${new Date(challenge.endsAt * 1000).toLocaleDateString()}`)); standings.append(heading)
  const eligible = (state.scores.get(game.gameId) ?? []).filter(entry => entry.at >= challenge.startsAt && entry.at <= challenge.endsAt)
  const ranked = boardFor(eligible, 'all', 50, Math.min(now, challenge.endsAt), game.scoreDir ?? 'desc')
  const list = el('ol', 'challenge-board')
  ranked.forEach((entry, index) => { const row = el('li'); row.append(el('span', 'rank', String(index + 1).padStart(2, '0')), playerLink(entry), el('small', '', new Date(entry.at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })), scoreLink(entry)); list.append(row) })
  if (!ranked.length) list.append(el('li', 'activity-empty', phase === 'FINAL' ? 'No verified scores from this window have been received.' : 'Waiting for the first verified score in this challenge window…'))
  standings.append(el('p', 'page-lede', 'Standings use the bounded history received from these relays. Earlier or missing events may change the result; complete challenge history has not been established.'));
  standings.append(list); main.append(standings); return main
}

async function shareInvitation(game: WebGame): Promise<void> {
  const signer = currentNostrSigner() ?? await connectIdentity(true)
  if (!signer) { toast('Connect a signing-capable Signet or Nostr session to create a verifiable invitation.', 'warn'); return }
  try {
    const signed = await signer.signEvent(invitationTemplate(game.gameId, game.url))
    if (!parseInvitation(signed)) throw new Error('invalid signed invitation')
    const url = `${location.origin}/invite/${encodeInvitation(signed)}`
    if (navigator.share) await navigator.share({ title: `Play ${game.name} on Gamestr`, text: `Join me for ${game.name}.`, url })
    else { await navigator.clipboard.writeText(url); toast('Signed invitation link copied.', 'good') }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return
    toast(error instanceof Error ? error.message : 'Invitation signing or sharing was cancelled.', 'warn')
  }
}

function createChallengeDialog(game: WebGame): void {
  const modal = el('div', 'challenge-modal'); modal.setAttribute('role', 'dialog'); modal.setAttribute('aria-modal', 'true'); modal.setAttribute('aria-labelledby', 'challenge-title')
  const form = el('form', 'challenge-form')
  const heading = el('h2', '', `Challenge players in ${game.name}`); heading.id = 'challenge-title'
  const lede = el('p', '', 'Scores already signed by players during the challenge window become the live standings. No registration or tournament database.')
  const nameLabel = el('label', '', 'Challenge name'); const name = el('input'); name.type = 'text'; name.required = true; name.maxLength = 60; name.placeholder = `${game.name} Open`; nameLabel.append(name)
  const durationLabel = el('label', '', 'Duration'); const duration = el('select')
  for (const [seconds, label] of [['3600', '1 HOUR'], ['86400', '24 HOURS'], ['604800', '7 DAYS']]) { const option = el('option', '', label); option.value = seconds; if (seconds === '86400') option.selected = true; duration.append(option) }
  durationLabel.append(duration)
  const status = el('output', 'studio-status'); status.setAttribute('aria-live', 'polite')
  const actions = el('div', 'hero-actions'); const create = button('SIGN & CREATE', 'primary', () => undefined); create.type = 'submit'; const cancel = button('CANCEL', 'secondary', () => modal.remove()); actions.append(create, cancel)
  form.append(el('p', 'kicker', 'SIGNED CHALLENGE'), heading, lede, nameLabel, durationLabel, actions, status); modal.append(form); document.body.append(modal); activateDialog(modal); name.focus()
  form.addEventListener('submit', async event => {
    event.preventDefault()
    const signer = currentNostrSigner() ?? await connectIdentity(true)
    if (!signer) { status.className = 'studio-status error'; status.textContent = 'Connect a signing-capable Signet or Nostr session before creating a challenge.'; return }
    create.disabled = true; status.className = 'studio-status'; status.textContent = 'Waiting for your Nostr signer…'
    try {
      const signed = await signer.signEvent(challengeTemplate(game.gameId, game.url, name.value, Number(duration.value)))
      if (!parseChallenge(signed)) throw new Error('Signer returned an invalid challenge.')
      const url = `${location.origin}/challenge/${encodeChallenge(signed)}`
      if (navigator.share) await navigator.share({ title: name.value.trim(), text: `Compete in ${name.value.trim()} on Gamestr.`, url })
      else { await navigator.clipboard.writeText(url); toast('Signed challenge link copied.', 'good') }
      modal.remove()
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') { create.disabled = false; return }
      status.className = 'studio-status error'; status.textContent = error instanceof Error ? error.message : 'Challenge creation failed.'; create.disabled = false
    }
  })
}

function importDevicePreferencesDialog(): void {
  const pubkey = state.pubkey
  if (!pubkey) return
  const guest = readSocialState()
  const modal = el('div', 'challenge-modal'); modal.setAttribute('role', 'dialog'); modal.setAttribute('aria-modal', 'true'); modal.setAttribute('aria-labelledby', 'device-preferences-title')
  const form = el('form', 'challenge-form')
  const title = el('h2', '', 'Import device preferences'); title.id = 'device-preferences-title'
  const description = el('p', '', `Copy this browser’s ${guest.favourites.length} guest favourites and ${guest.follows.length} arcade follows into your connected account. The guest view keeps its copy. These stay local unless you choose to sync.`)
  const status = el('output', 'studio-status'); status.setAttribute('aria-live', 'polite')
  const submit = button('COPY TO MY ACCOUNT', 'primary', () => undefined); submit.type = 'submit'; submit.disabled = !guest.favourites.length && !guest.follows.length
  form.append(title, description, submit, button('CANCEL', 'secondary', () => modal.remove()), status); modal.append(form); document.body.append(modal); activateDialog(modal)
  form.addEventListener('submit', async event => {
    event.preventDefault(); if (submit.disabled || state.pubkey !== pubkey) return
    submit.disabled = true
    try {
      const { mergePreferences } = await import('./preference-sync')
      if (!modal.isConnected) return
      if (state.pubkey !== pubkey) { status.textContent = 'Your account changed. Close this dialog and choose the account to import into.'; return }
      state.social = mergePreferences(state.social, guest)
      const saved = writeSocialState(state.social, undefined, pubkey)
      state.accountMenuOpen = false
      render()
      modal.remove()
      toast(saved ? 'Device preferences copied to this account on this browser.' : 'Preferences copied for this visit; browser storage is unavailable.', saved ? 'good' : 'warn')
    } catch { status.textContent = 'Could not load the import. Your preferences are unchanged; please retry.' }
    finally { submit.disabled = false }
  })
}

function preferenceDialog(): void {
  const pubkey = state.pubkey
  if (!pubkey) return
  const modal = el('div', 'challenge-modal'); modal.setAttribute('role', 'dialog'); modal.setAttribute('aria-modal', 'true'); modal.setAttribute('aria-labelledby', 'preference-title')
  const form = el('form', 'challenge-form')
  const heading = el('h2', '', 'Sync arcade preferences'); heading.id = 'preference-title'
  const disclosure = el('p', '', 'This merges favourites and arcade follows from this account, then publishes them as public Nostr data. It never changes your NIP-02 follow list. Local preferences remain available if relays are offline.')
  const label = el('label'); const consent = el('input'); consent.type = 'checkbox'; consent.required = true; label.append(consent, el('span', '', 'I agree to publish my favourites and arcade follows publicly.'))
  const status = el('output', 'studio-status'); status.setAttribute('aria-live', 'polite')
  const submit = button('MERGE & PUBLISH', 'primary', () => undefined); submit.type = 'submit'
  form.append(heading, disclosure, label, submit, button('CANCEL', 'secondary', () => modal.remove()), status); modal.append(form); document.body.append(modal); activateDialog(modal)
  form.addEventListener('submit', async event => {
    event.preventDefault(); if (!consent.checked || submit.disabled) return
    submit.disabled = true; status.textContent = 'Loading this account’s arcade preferences…'
    let savedLocally = false
    const approved = () => modal.isConnected && consent.checked && state.pubkey === pubkey
    try {
      const { loadRemotePreferences, mergePreferences, publishPreferences } = await import('./preference-sync')
      const remote = await loadRemotePreferences(pubkey, RELAYS)
      if (!approved()) return
      state.social = mergePreferences(state.social, remote)
      savedLocally = writeSocialState(state.social, undefined, pubkey)
      const signer = currentNostrSigner() ?? await connectIdentity(true)
      if (!signer || !approved()) throw new Error('Connect a signer for this account to publish.')
      const count = await publishPreferences(state.social, pubkey, signer, RELAYS, approved)
      status.textContent = count ? `Merged preferences accepted by ${count} relay${count === 1 ? '' : 's'}.${savedLocally ? '' : ' Browser storage is unavailable.'}` : `${savedLocally ? 'Saved locally' : 'Available for this visit; browser storage is unavailable'}; no relay confirmed publication. Retry when online.`
      render()
    } catch (error) { status.textContent = error instanceof Error ? error.message : savedLocally ? 'Preferences remain saved locally.' : 'Could not sync preferences. Browser storage is unavailable.' }
    finally { submit.disabled = false }
  })
}

function rewardDialog(pubkey: string, profile: Profile): void {
  const lightning = lightningDestination(profile)
  if (!lightning) return
  const modal = el('div', 'challenge-modal'); modal.setAttribute('role', 'dialog'); modal.setAttribute('aria-modal', 'true'); modal.setAttribute('aria-labelledby', 'reward-title')
  const form = el('form', 'challenge-form reward-form')
  const identity = playerIdentity(pubkey, latestPlayerEntry(pubkey), profile)
  const heading = el('h2', '', `Zap ${identity.label}`); heading.id = 'reward-title'
  const lede = el('p', '', 'Pay with your own wallet by QR, Lightning link, or WebLN to the player’s lud16 Lightning address or lud06 LNURL. This arcade never sees wallet credentials or routes this through the cabinet wallet.')
  const amountLabel = el('label', '', 'Amount in sats'); const amount = el('input'); amount.type = 'number'; amount.min = '1'; amount.max = '100000'; amount.step = '1'; amount.required = true; amount.value = '21'; amountLabel.append(amount)
  const presets = el('div', 'reward-presets'); for (const value of [21, 100, 500, 1000]) presets.append(button(`${value} SATS`, 'secondary', () => { amount.value = String(value) }))
  const zapLabel = el('label', 'zap-option'); const publicZap = el('input'); publicZap.type = 'checkbox'; zapLabel.append(publicZap, el('span', '', 'Create a public Nostr zap receipt (reveals your signing identity)'))
  const destination = el('p', 'reward-destination', `TO ${lightning}`)
  const privacy = el('p', 'reward-privacy', 'Your browser contacts the recipient’s Lightning service only after you approve. The wallet still shows the final invoice for authorization.')
  const status = el('output', 'studio-status'); status.setAttribute('aria-live', 'polite')
  const actions = el('div', 'hero-actions'); const pay = button('OPEN MY WALLET', 'primary', () => undefined); pay.type = 'submit'; const cancel = button('CANCEL', 'secondary', () => modal.remove()); actions.append(pay, cancel)
  form.append(el('p', 'kicker', 'USER-OWNED LIGHTNING'), heading, lede, amountLabel, presets, zapLabel, destination, privacy, actions, status); modal.append(form); document.body.append(modal); activateDialog(modal); amount.focus(); amount.select()
  let invoiceCreated = false
  form.addEventListener('submit', async event => {
    event.preventDefault(); const provider = (window as NostrWindow).webln
    if (pay.disabled) return
    pay.disabled = true; status.className = 'studio-status'; status.textContent = 'Requesting an exact-amount invoice from the player’s Lightning address…'
    try {
      const signer = publicZap.checked ? currentNostrSigner() ?? await connectIdentity(true) : undefined
      if (publicZap.checked && !signer) throw new Error('A signing-capable Signet or Nostr session is required for a public zap receipt.')
      if (!modal.isConnected) return
      const invoice = await requestLightningInvoice(lightning, Number(amount.value), fetch, publicZap.checked ? { zap: { recipientPubkey: pubkey, signer: signer!, relays: RELAYS } } : {})
      if (!modal.isConnected) return
      invoiceCreated = true
      amount.disabled = true; publicZap.disabled = true
      for (const preset of presets.querySelectorAll('button')) preset.disabled = true
      const { default: qrcode } = await import('qrcode-generator')
      if (!modal.isConnected) return
      const qr = qrcode(0, 'M'); qr.addData(invoice.toUpperCase()); qr.make()
      const image = el('img', 'reward-qr'); image.alt = `Lightning invoice for ${Number(amount.value)} sats to ${identity.label}`
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qr.createSvgTag({ scalable: true }))}`
      const walletLink = el('a', 'primary', 'OPEN LIGHTNING WALLET'); walletLink.href = `lightning:${invoice}`
      const copy = button('COPY INVOICE', 'secondary', () => {
        void (navigator.clipboard?.writeText(invoice) ?? Promise.reject(new Error('Clipboard unavailable'))).then(() => toast('Invoice copied. Pay this invoice once.', 'good'), () => { status.textContent = 'Copy the invoice text below; clipboard access is unavailable.' })
      })
      const invoiceText = el('textarea'); invoiceText.readOnly = true; invoiceText.value = invoice; invoiceText.setAttribute('aria-label', 'Exact Lightning invoice')
      form.append(image, walletLink, copy, invoiceText)
      pay.textContent = 'INVOICE READY'
      status.textContent = 'Invoice ready. Pay once in your wallet. Settlement has not been confirmed here.'
      if (provider) {
        if (!modal.isConnected) return
        await provider.enable()
        if (!modal.isConnected) return
        status.textContent = 'Waiting for your wallet. If interrupted, check payment history before paying this invoice again.'
        await provider.sendPayment(invoice)
        status.className = 'studio-status success'
        status.textContent = 'Your wallet returned a payment result. Check your wallet for confirmed settlement.'
      }
    } catch (error) {
      status.className = 'studio-status error'; status.textContent = error instanceof Error ? error.message : 'Lightning reward failed or was cancelled.'; pay.disabled = invoiceCreated
      if (invoiceCreated) status.textContent += ' Check your wallet before retrying. Use the same invoice above; no new invoice was requested.'
    }
  })
}

function footer(): HTMLElement {
  const foot = el('footer'); const brand = el('div'); brand.append(el('strong', '', WEB_EDITION.footerName), el('p', '', WEB_EDITION.footerDescription))
  const links = el('div'); links.append(linkButton('ARCADE', '/'), linkButton('SCORES', '/scores'), linkButton('BUILD', '/developers'))
  links.append(externalLink('GAMESTR.IO ↗', GAMESTR_ORIGIN))
  if (WEB_EDITION.key === '600') {
    const definitions = el('a', 'text-link', 'GAME DEFINITIONS')
    definitions.href = '/definitions/600/'
    links.append(definitions)
  }
  if (WEB_EDITION.footerLink) {
    const external = el('a', 'text-link', WEB_EDITION.footerLink.label)
    external.href = WEB_EDITION.footerLink.url
    external.target = '_blank'
    external.rel = 'noopener noreferrer'
    links.append(external)
  }
  foot.append(brand, links, el('small', '', 'No custodial account · No nsec collection · No score database'))
  return foot
}

function toast(message: string, tone: 'good' | 'warn'): void {
  document.querySelector('.toast')?.remove(); const node = el('div', `toast ${tone}`, message); node.setAttribute('role', 'status'); document.body.append(node); setTimeout(() => node.remove(), 5000)
}

function updateMetadata(): void {
  const current = route()
  const game = current.name === 'game' ? state.games.find(item => item.slug === current.slug) : undefined
  const title = game ? `${game.name} - ${WEB_EDITION.titleSuffix}` : current.name === 'scores' ? `Verified scores - ${WEB_EDITION.titleSuffix}` : current.name === 'developers' ? 'Build for Gamestr' : current.name === 'challenge' ? `Signed challenge - ${WEB_EDITION.titleSuffix}` : current.name === 'invite' ? `Game invitation - ${WEB_EDITION.titleSuffix}` : current.name === 'player' ? `Nostr player - ${WEB_EDITION.titleSuffix}` : WEB_EDITION.siteTitle
  const description = game?.description ?? game?.tagline ?? WEB_EDITION.siteDescription
  document.title = title
  const canonical = `${location.origin}${location.pathname === '/' ? '/' : location.pathname}`
  document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', canonical)
  for (const [selector, value] of [['meta[name="description"]', description], ['meta[property="og:title"]', title], ['meta[property="og:description"]', description], ['meta[property="og:url"]', canonical]]) document.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', value)
}

function render(): void {
  const focused = document.activeElement instanceof HTMLElement && app.contains(document.activeElement) ? document.activeElement : undefined
  const focusId = focused?.id
  const selection = focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement ? [focused.selectionStart, focused.selectionEnd] : undefined
  updateMetadata()
  const current = route()
  const content = current.name === 'home' ? home()
    : current.name === 'scores' ? scoresPage()
      : current.name === 'developers' ? developersPage()
        : current.name === 'game' ? gamePage(current.slug!)
          : current.name === 'player' ? playerPage(current.id!)
            : current.name === 'score' ? scorePage(current.id!)
              : current.name === 'invite' ? invitationPage(current.id!)
                : challengePage(current.id!)
  app.replaceChildren(header(), content, footer())
  const replacement = focusId ? document.getElementById(focusId) ?? (focusId.startsWith('account-') ? document.getElementById('account-menu-trigger') : undefined) : undefined
  const target = focused?.isConnected ? focused : replacement
  target?.focus({ preventScroll: true })
  if (selection && (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
    try { target.setSelectionRange(selection[0], selection[1]) } catch { /* non-text controls */ }
  }
}

function refreshScores(): void {
  const current = route()
  if (current.name === 'developers') return
  if (current.name !== 'home') { render(); return }
  renderGameGrid()
  refreshActivityPanel()
}

function refreshRelayStatus(): void {
  const pill = document.querySelector<HTMLElement>('.relay-pill')
  if (!pill) return
  pill.className = `relay-pill ${state.relay}`
  pill.textContent = state.relay === 'up' ? 'NOSTR LIVE' : state.relay === 'down' ? (state.cachedScores ? 'CACHED · RELAYS OFFLINE' : 'RELAYS OFFLINE') : (state.cachedScores ? 'CACHED · CONNECTING' : 'CONNECTING')
}

async function boot(): Promise<void> {
  try {
    const response = await fetch('/catalogue.json')
    if (!response.ok) throw new Error(`catalogue ${response.status}`)
    state.games = await response.json() as WebGame[]
  } catch {
    app.replaceChildren(el('main', 'fatal', 'The arcade catalogue could not be loaded. Please retry.'))
    return
  }
  let cacheStorage: Storage | undefined
  try { cacheStorage = localStorage } catch { /* live-only when storage is blocked */ }
  const feed = createGamestrCatalogue(RELAYS, { cacheStorage, onCached: () => { state.cachedScores = true }, scoreAuthorities: Object.fromEntries(state.games.map(game => [game.gameId, game.scoreAuthorities ?? []])), onStatus: status => { state.relay = status; refreshRelayStatus() } })
  for (const game of state.games) {
    feed.subscribe(game.gameId, entries => { state.scores.set(game.gameId, entries); refreshScores() }, { kind: game.scoreKind, field: game.scoreField, dir: game.scoreDir })
  }
  window.addEventListener('popstate', render)
  window.addEventListener('beforeunload', () => feed.dispose(), { once: true })
  if ('serviceWorker' in navigator && import.meta.env.PROD) void navigator.serviceWorker.register('/sw.js')
  render()
  if (WEB_EDITION.key === '600') {
    void loadWellKnownMembers().then(members => {
      state.members = members
      render()
    }).catch(() => undefined)
  }
  try { localStorage.removeItem('gamestr:pubkey') } catch { /* signing still works in memory when browser storage is blocked */ }
  void restoreNostrAccess(RELAYS).finally(() => {
    state.identityRestoring = false
    render()
  })
}

void boot()
